// src/components/Navbar.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpenText,
  CalendarHeart,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Eye,
  Heart,
  Home,
  Layers3,
  Languages,
  MoonStar,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import HamburgerIcon from "./HamburgerIcon";
import * as cardsModule from "./card-data";
import { cardPath } from "../utils/cardRoutes";
import { translateCardName } from "../utils/i18n";

function getCardsArray(mod) {
  const candidate =
    mod?.default ?? mod?.cardData ?? mod?.cards ?? mod?.data ?? null;
  if (Array.isArray(candidate)) return candidate;
  if (candidate && typeof candidate === "object") return Object.values(candidate);
  const firstArray = Object.values(mod || {}).find(Array.isArray);
  return Array.isArray(firstArray) ? firstArray : [];
}

const normalizeSearchText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const ZODIAC_SIGNS = [
  { name: "Aries", es: "Aries", slug: "aries" },
  { name: "Taurus", es: "Tauro", slug: "taurus" },
  { name: "Gemini", es: "Geminis", slug: "gemini" },
  { name: "Cancer", es: "Cancer", slug: "cancer" },
  { name: "Leo", es: "Leo", slug: "leo" },
  { name: "Virgo", es: "Virgo", slug: "virgo" },
  { name: "Libra", es: "Libra", slug: "libra" },
  { name: "Scorpio", es: "Escorpio", slug: "scorpio" },
  { name: "Sagittarius", es: "Sagitario", slug: "sagittarius" },
  { name: "Capricorn", es: "Capricornio", slug: "capricorn" },
  { name: "Aquarius", es: "Acuario", slug: "aquarius" },
  { name: "Pisces", es: "Piscis", slug: "pisces" },
];

const EMOTIONS = [
  { name: "Uncertain Love", es: "Amor incierto" },
  { name: "Heart Broken", es: "Corazon roto" },
  { name: "Anxious", es: "Ansiedad" },
  { name: "Confused", es: "Confusion" },
  { name: "Lonely", es: "Soledad" },
  { name: "Hopeless", es: "Desesperanza" },
  { name: "Guilty", es: "Culpa" },
  { name: "Betrayed", es: "Traicion" },
  { name: "Insecure", es: "Inseguridad" },
  { name: "Overwhelmed", es: "Agobio" },
  { name: "Other", es: "Otra emocion" },
];

const TAROT_SUITS = [
  { name: "Major Arcana", es: "Arcanos mayores", slug: "/tarot/major-arcana" },
  { name: "Wands", es: "Bastos", slug: "/tarot/wands" },
  { name: "Cups", es: "Copas", slug: "/tarot/cups" },
  { name: "Swords", es: "Espadas", slug: "/tarot/swords" },
  { name: "Pentacles", es: "Oros", slug: "/tarot/pentacles" },
  { name: "All Cards", es: "Todas las cartas", slug: "/tarot-cards" },
];

export default function Navbar() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileEmotions, setShowMobileEmotions] = useState(false);
  const [showMobileCards, setShowMobileCards] = useState(false);
  const [showMobileTarotscope, setShowMobileTarotscope] = useState(false);

  const [showEmotions, setShowEmotions] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [showTarotscope, setShowTarotscope] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const formRef = useRef(null);
  const searchFormRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const emotionsTimeout = useRef(null);
  const cardsTimeout = useRef(null);
  const tarotscopeTimeout = useRef(null);
  const cards = useMemo(() => getCardsArray(cardsModule), []);
  const isSpanishPath = location.pathname === "/es" || location.pathname.startsWith("/es/");

  const toLocalizedPath = (to) => {
    if (!isSpanishPath) return to;
    if (to === "/") return "/es";
    return `/es${to.startsWith("/") ? to : `/${to}`}`;
  };

  const languageTarget = (() => {
    const path = location.pathname || "/";
    const suffix = `${location.search || ""}${location.hash || ""}`;
    if (isSpanishPath) {
      const englishPath = path === "/es" ? "/" : path.replace(/^\/es(?=\/)/, "");
      return `${englishPath || "/"}${suffix}`;
    }
    return `${path === "/" ? "/es" : `/es${path}`}${suffix}`;
  })();
  const languageLabel = isSpanishPath ? "Switch to English" : "Cambiar a espa\u00f1ol";
  const languageCode = isSpanishPath ? "EN" : "ES";
  const copy = isSpanishPath
    ? {
        home: "Inicio",
        whatIsTarot: "Tarot?",
        cards: "Cartas",
        emotions: "Emociones",
        yesNo: "Si / No",
        readMyCard: "Leer mis cartas",
        tarotscope: "Tarotscopo",
        fateFriday: "Viernes del destino",
        fullMoon: "Luna llena",
        searchCards: "Buscar cartas",
        searchTarotCards: "Buscar cartas del tarot",
        submitSearch: "Buscar",
        openSearch: "Abrir busqueda",
        openMenu: "Abrir menu",
        closeMenu: "Cerrar menu",
        tarotCards: "Cartas del tarot",
      }
    : {
        home: "Home",
        whatIsTarot: "What Is Tarot?",
        cards: "Cards",
        emotions: "Emotions",
        yesNo: "Yes/No",
        readMyCard: "Read My Card",
        tarotscope: "Tarotscope",
        fateFriday: "Fate Friday",
        fullMoon: "Full Moon",
        searchCards: "Search cards",
        searchTarotCards: "Search tarot cards",
        submitSearch: "Submit search",
        openSearch: "Open search",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        tarotCards: "Tarot Cards",
      };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`${toLocalizedPath("/search")}?query=${encodeURIComponent(q)}`);
    setSearchQuery("");
    setSuggestions([]);
    setSearchOpen(false);
  };

  const handleSubmitSearch = (term) => {
    const q = term.trim();
    if (!q) return;
    navigate(`${toLocalizedPath("/search")}?query=${encodeURIComponent(q)}`);
    setSearchQuery("");
    setSuggestions([]);
    setShowMobileMenu(false);
  };

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!desktopSearchRef.current) return;
      const clickedDesktopSearch = desktopSearchRef.current.contains(e.target);
      const clickedMobileSearch = formRef.current?.contains(e.target);

      if (!clickedDesktopSearch && !clickedMobileSearch) {
        setIsFocused(false);
        setSearchOpen(false);
        setSuggestions([]);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!formRef.current) return;
      if (!formRef.current.contains(e.target)) setSuggestions([]);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [showMobileMenu]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    if (showMobileMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMobileMenu]);

  useEffect(() => {
    const q = normalizeSearchText(searchQuery.trim());
    if (!q) {
      setSuggestions([]);
      return;
    }

    const scored = (Array.isArray(cards) ? cards : [])
      .filter((c) => c?.name)
      .map((c) => {
        const name = normalizeSearchText(c.name);
        const displayName = isSpanishPath ? translateCardName(c.name) : c.name;
        const localizedName = normalizeSearchText(displayName);
        let score = 0;
        if (localizedName.startsWith(q)) score += 120;
        if (localizedName.includes(q)) score += 20;
        if (name.startsWith(q)) score += 100;
        if (name.includes(q)) score += 10;
        return { ...c, _score: score, _displayName: displayName };
      })
      .filter((c) => c._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 8);

    setSuggestions(scored);
  }, [searchQuery, cards, isSpanishPath]);

  const navLinkClass =
    "relative inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-md px-1.5 text-[15px] font-bold leading-none tracking-[0.02em] text-white/95 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 min-[1400px]:px-2 2xl:px-3 2xl:text-[17px]";
  const desktopLogoClass =
    "mr-3 flex h-[68px] w-[166px] shrink-0 items-center justify-center rounded-md px-1 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 min-[1400px]:h-[78px] min-[1400px]:w-[192px] 2xl:w-[214px]";
  const navButtonClass = `${navLinkClass} gap-1.5`;
  const activeNavButtonClass =
    "!bg-[#06243b] !text-white hover:!bg-[#06243b] shadow-[0_14px_34px_rgba(2,10,26,0.28)] ring-1 ring-[#9ce7ee]/20 after:absolute after:-bottom-2 after:left-1/2 after:h-2 after:w-10 after:-translate-x-1/2 after:rounded-t-md after:border-x after:border-t after:border-[#9ce7ee]/20 after:bg-[#06243b]";
  const menuClass =
    "absolute left-0 top-full z-[310] mt-2 min-w-[230px] overflow-hidden rounded-lg border border-[#9ce7ee]/20 bg-[linear-gradient(180deg,#06243b_0%,#061a2e_100%)] text-white shadow-[0_24px_64px_rgba(2,10,26,0.52)] ring-1 ring-white/10 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-[linear-gradient(90deg,transparent,rgba(156,231,238,0.55),transparent)]";
  const rightMenuClass =
    "absolute right-0 top-full z-[310] mt-2 overflow-hidden rounded-lg border border-[#9ce7ee]/20 bg-[linear-gradient(180deg,#06243b_0%,#061a2e_100%)] text-white shadow-[0_24px_64px_rgba(2,10,26,0.52)] ring-1 ring-white/10 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-[linear-gradient(90deg,transparent,rgba(156,231,238,0.55),transparent)]";
  const menuLinkClass =
    "group flex w-full min-w-0 items-center justify-between gap-4 px-4 py-3 text-[17px] font-semibold normal-case tracking-[0.015em] text-white/85 transition hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white focus:outline-none";
  const menuArrowClass =
    "shrink-0 text-[#9ce7ee]/70 opacity-80 transition group-hover:translate-x-0.5 group-hover:text-[#9ce7ee] group-hover:opacity-100";
  const mobileLinkClass =
    "group flex min-h-[54px] w-full items-center justify-start gap-3 rounded-md px-3 text-left text-[18px] font-bold leading-none tracking-[0.035em] text-white/95 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";
  const activeMobileLinkClass =
    "!bg-white/10 !text-white hover:!bg-white/10 shadow-[0_10px_24px_rgba(2,10,26,0.16)]";
  const mobileIconClass =
    "flex h-8 w-7 shrink-0 items-center justify-center text-[#d8c8ec]/70 opacity-75 transition group-hover:text-[#f0e6ff]/90 group-hover:opacity-95";
  const mobileLabelClass =
    "flex min-w-0 flex-1 items-center justify-start gap-2 text-left";
  const mobilePanelClass =
    "mt-2 overflow-hidden rounded-lg border border-white/15 bg-[linear-gradient(180deg,#061f34_0%,#051729_100%)] shadow-[0_18px_42px_rgba(2,10,26,0.3)] ring-1 ring-[#9ce7ee]/15";
  const mobilePanelLinkClass =
    "group flex items-center justify-start gap-3 px-3 py-3 text-left text-[17px] font-semibold normal-case tracking-[0.015em] text-white/85 transition hover:bg-white/10 hover:text-white";
  const mobileMenuListClass =
    "w-full max-w-[360px] [&>*+*]:relative [&>*+*]:mt-1 [&>*+*]:pt-1 [&>*+*]:before:absolute [&>*+*]:before:left-12 [&>*+*]:before:right-3 [&>*+*]:before:top-0 [&>*+*]:before:h-px [&>*+*]:before:bg-[linear-gradient(90deg,rgba(216,200,236,0.28),rgba(156,231,238,0.14),transparent)]";
  const mobileSubCueClass =
    "h-0 w-0 shrink-0 border-y-[4px] border-l-[6px] border-y-transparent border-l-[#d8c8ec]/55 opacity-80 transition group-hover:border-l-[#f0e6ff]/90 group-hover:opacity-100";
  const desktopLanguageClass =
    "group flex h-10 min-w-10 shrink-0 items-center justify-center gap-1.5 rounded-md border border-white/25 bg-white/10 px-2.5 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.10)] transition hover:border-[#f5d37b]/60 hover:bg-[#08243a] hover:text-[#f5d37b] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";
  const mobileLanguageClass =
    "inline-flex h-10 w-20 shrink-0 items-center justify-center gap-1.5 self-end rounded-lg border border-white/25 bg-[#061f34]/70 text-[14px] font-black tracking-[0.06em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_10px_26px_rgba(2,10,26,0.18)] ring-1 ring-[#9ce7ee]/10 transition hover:border-[#f5d37b]/55 hover:text-[#f5d37b] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";

  return (
    <>
      <nav className="hidden min-[1180px]:block relative z-[300] px-2 pt-4 min-[1400px]:px-4">
        <div
          ref={desktopSearchRef}
          className="mx-auto w-fit max-w-[calc(100vw-1rem)] overflow-visible rounded-lg border border-[#9ce7ee]/70 bg-[linear-gradient(120deg,rgba(2,22,48,0.86),rgba(0,92,131,0.78),rgba(93,38,96,0.72))] shadow-[0_18px_60px_rgba(2,22,48,0.26)] backdrop-blur-2xl"
        >
          <div className="flex items-center gap-2 px-2 py-2 min-[1400px]:px-3 xl:px-4">
            <div className="flex min-w-0 flex-nowrap items-center justify-start gap-0 overflow-visible pr-2 font-[Cormorant_Garamond] min-[1400px]:pr-3">
              <Link to={toLocalizedPath("/")} className={desktopLogoClass} aria-label={copy.home}>
                <img
                  src="/icons/logo.webp"
                  alt="TarotFate"
                  className="max-h-[64px] w-auto max-w-full object-contain min-[1400px]:max-h-[74px]"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </Link>
              <Link to={toLocalizedPath("/what-is-tarot-cards-complete-guide")} className={navLinkClass}>
                {copy.whatIsTarot}
              </Link>

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  clearTimeout(cardsTimeout.current);
                  setShowCards(true);
                }}
                onMouseLeave={() => {
                  cardsTimeout.current = setTimeout(() => setShowCards(false), 120);
                }}
              >
                <button
                  type="button"
                  className={`${navButtonClass} ${showCards ? activeNavButtonClass : ""}`}
                  aria-haspopup="true"
                  aria-expanded={showCards ? "true" : "false"}
                >
                  {copy.cards}
                  <ChevronDown
                    size={16}
                    strokeWidth={1.8}
                    className={`transition-transform ${showCards ? "rotate-180 text-[#9ce7ee]" : ""}`}
                  />
                </button>
                {showCards && (
                  <div className={menuClass}>
                    <ul className="divide-y divide-white/10 py-1">
                      {TAROT_SUITS.map((item) => (
                        <li key={item.slug}>
                          <Link to={toLocalizedPath(item.slug)} className={menuLinkClass}>
                            <span className="min-w-0 truncate">{isSpanishPath ? item.es : item.name}</span>
                            <ChevronRight size={15} strokeWidth={1.8} className={menuArrowClass} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  clearTimeout(emotionsTimeout.current);
                  setShowEmotions(true);
                }}
                onMouseLeave={() => {
                  emotionsTimeout.current = setTimeout(() => setShowEmotions(false), 120);
                }}
              >
                <button
                  type="button"
                  className={`${navButtonClass} ${showEmotions ? activeNavButtonClass : ""}`}
                  aria-haspopup="true"
                  aria-expanded={showEmotions ? "true" : "false"}
                >
                  {copy.emotions}
                  <ChevronDown
                    size={16}
                    strokeWidth={1.8}
                    className={`transition-transform ${showEmotions ? "rotate-180 text-[#9ce7ee]" : ""}`}
                  />
                </button>
                {showEmotions && (
                  <div className={`${menuClass} grid w-[min(30rem,calc(100vw-2rem))] grid-cols-2`}>
                    {EMOTIONS.map((emotion) => {
                      const slug = emotion.name.toLowerCase().replace(/ /g, "-");
                      return (
                        <Link
                          key={emotion.name}
                          to={toLocalizedPath(`/emotions/${slug}`)}
                          className={`${menuLinkClass} border-b border-white/10 even:border-l even:border-l-white/10`}
                        >
                          <span className="min-w-0 truncate">{isSpanishPath ? emotion.es : emotion.name}</span>
                          <ChevronRight size={15} strokeWidth={1.8} className={menuArrowClass} />
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              <Link to={toLocalizedPath("/yes-no-tarot")} className={navLinkClass}>{copy.yesNo}</Link>
              <Link to={toLocalizedPath("/readmycard")} className={navLinkClass}>{copy.readMyCard}</Link>

              <div
                className="relative shrink-0"
                onMouseEnter={() => {
                  clearTimeout(tarotscopeTimeout.current);
                  setShowTarotscope(true);
                }}
                onMouseLeave={() => {
                  tarotscopeTimeout.current = setTimeout(() => setShowTarotscope(false), 120);
                }}
              >
                <button
                  type="button"
                  className={`${navButtonClass} ${showTarotscope ? activeNavButtonClass : ""}`}
                  aria-haspopup="true"
                  aria-expanded={showTarotscope ? "true" : "false"}
                >
                  {copy.tarotscope}
                  <ChevronDown
                    size={16}
                    strokeWidth={1.8}
                    className={`transition-transform ${showTarotscope ? "rotate-180 text-[#9ce7ee]" : ""}`}
                  />
                </button>
                {showTarotscope && (
                  <div className={`${rightMenuClass} w-[min(30rem,calc(100vw-2rem))]`}>
                    <div className="grid grid-cols-2 py-1">
                      {ZODIAC_SIGNS.map((sign) => (
                        <Link
                          key={sign.slug}
                          to={toLocalizedPath(`/tarotscope/${sign.slug}`)}
                          className={`${menuLinkClass} border-b border-white/10 even:border-l even:border-l-white/10`}
                        >
                          <span className="min-w-0 truncate">{isSpanishPath ? sign.es : sign.name}</span>
                          <ChevronRight size={15} strokeWidth={1.8} className={menuArrowClass} />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to={toLocalizedPath("/fate-friday")} className={navLinkClass}>{copy.fateFriday}</Link>
              <Link to={toLocalizedPath("/full-moon")} className={navLinkClass}>{copy.fullMoon}</Link>
            </div>

            <div
              className="relative ml-1 flex shrink-0 items-center justify-end gap-2 border-l border-white/20 pl-3 font-[Cormorant_Garamond]"
            >
              <button
                type="button"
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md border text-white transition ${
                  searchOpen
                    ? "border-[#9ce7ee]/55 bg-[#08243a] text-[#f5d37b]"
                    : "border-white/25 bg-white/10 hover:border-white/40 hover:bg-white/15"
                }`}
                aria-label={copy.openSearch}
                aria-expanded={searchOpen ? "true" : "false"}
                onClick={() => {
                  setSearchOpen((open) => !open);
                  setIsFocused(true);
                  setTimeout(() => searchFormRef.current?.querySelector("input")?.focus(), 10);
                }}
              >
                <Search size={18} />
              </button>
              <Link
                to={languageTarget}
                className={desktopLanguageClass}
                aria-label={languageLabel}
                title={languageLabel}
              >
                <Languages size={17} strokeWidth={1.8} aria-hidden="true" />
                <span className="text-[12px] font-black leading-none tracking-[0.08em]">{languageCode}</span>
              </Link>
            </div>
          </div>

          {searchOpen && (
            <div className="flex justify-end border-t border-white/10 px-4 pb-4 pt-3">
              <div className="relative w-[min(28rem,calc(100vw-3rem))]">
                <form
                  ref={searchFormRef}
                  onSubmit={handleSearch}
                  className="flex h-12 min-w-0 items-center rounded-lg border border-white/30 bg-[#08243a]/95 px-3 shadow-[inset_0_0_0_1px_rgba(156,231,238,0.14)] ring-1 ring-[#9ce7ee]/15 focus-within:border-[#9ce7ee]/70"
                  aria-label="Site search"
                  onFocus={() => setIsFocused(true)}
                >
                  <Search size={19} className="mr-2 shrink-0 text-white/85" />
                  <input
                    type="text"
                    placeholder={copy.searchCards}
                    className="min-w-0 flex-1 bg-transparent text-[17px] font-semibold text-white outline-none placeholder:text-white/60"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setIsFocused(false);
                        setSearchOpen(false);
                        e.currentTarget.blur();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white/85 transition hover:bg-white/10 hover:text-white"
                    aria-label={copy.closeMenu}
                    onClick={() => {
                      setSearchOpen(false);
                      setIsFocused(false);
                      setSearchQuery("");
                      setSuggestions([]);
                    }}
                  >
                    <X size={18} />
                  </button>
                </form>

                {isFocused && searchQuery && suggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-[320] mt-2 max-w-full overflow-hidden rounded-lg border border-white/20 bg-[#061f34] shadow-[0_22px_60px_rgba(2,10,26,0.5)] ring-1 ring-[#9ce7ee]/20">
                    <ul className="max-h-96 overflow-auto divide-y divide-white/10 py-1" role="listbox">
                      {suggestions.map((s) => (
                        <li key={s.slug} role="option" aria-selected="false">
                          <Link
                            to={toLocalizedPath(cardPath(s.slug))}
                            className="flex min-w-0 items-center gap-3 px-3 py-2.5 text-white/90 transition hover:bg-white/10 hover:text-white"
                            onClick={() => {
                              setSearchQuery("");
                              setSuggestions([]);
                              setIsFocused(false);
                              setSearchOpen(false);
                            }}
                          >
                            {s.image || s.img || s.thumbnail ? (
                              <img
                                src={s.image || s.img || s.thumbnail}
                                alt={s._displayName || s.name}
                                className="h-8 w-auto shrink-0 rounded-sm"
                                loading="lazy"
                              />
                            ) : (
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-white/10 text-[10px] uppercase">
                                {copy.cards}
                              </span>
                            )}
                            <span className="min-w-0 truncate text-[16px] font-semibold">{s._displayName || s.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {!showMobileMenu && (
        <div className="min-[1180px]:hidden absolute right-4 top-24 z-[330]">
          <button
            onClick={() => setShowMobileMenu(true)}
            aria-label={copy.openMenu}
            className="group flex h-12 w-12 items-center justify-center text-white drop-shadow-[0_10px_22px_rgba(2,10,26,0.42)] transition hover:text-[#9ce7ee] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <HamburgerIcon />
          </button>
        </div>
      )}

      {showMobileMenu && (
        <div
          className="mobile-drawer-overlay fixed inset-0 z-[300] bg-[#021021]/58"
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      {showMobileMenu && (
        <aside
          ref={menuRef}
          className="mobile-drawer-panel fixed right-0 top-0 z-[320] flex h-[100dvh] w-[min(88vw,430px)] max-w-[100vw] flex-col overflow-x-hidden overflow-y-auto border-l border-white/25 bg-[#053354] px-5 pb-8 pt-44 font-[Cormorant_Garamond] text-white shadow-[0_0_80px_rgba(0,0,0,0.42)] animate-slideInRight"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(1, 45, 76, 0.78), rgba(8, 45, 83, 0.82) 48%, rgba(83, 24, 61, 0.78)), url('/icons/drawer.webp')",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div
            ref={formRef}
            className="absolute left-5 right-4 top-10 z-[340] flex min-w-0 flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <div className="flex min-w-0 flex-1 justify-end">
                <Link
                  to={languageTarget}
                  onClick={() => setShowMobileMenu(false)}
                  className={mobileLanguageClass}
                  aria-label={languageLabel}
                  title={languageLabel}
                >
                  <Languages size={16} strokeWidth={1.8} aria-hidden="true" />
                  <span>{languageCode}</span>
                </Link>
              </div>
              <span className="h-12 w-12 shrink-0" aria-hidden="true" />
            </div>

            <div className="mt-1 flex items-center gap-3">
              <div className="relative min-w-0 flex-1">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitSearch(searchQuery);
                  }}
                  className="flex h-12 items-center rounded-lg border border-white/20 bg-[#061f34]/70 px-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_14px_34px_rgba(2,10,26,0.2)] ring-1 ring-[#9ce7ee]/10 focus-within:border-white/55"
                >
                  <input
                    type="text"
                    placeholder={copy.searchTarotCards}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-[17px] font-semibold normal-case tracking-normal text-white outline-none placeholder:text-white/60"
                  />
                  <button
                    type="submit"
                    className="flex h-9 w-9 items-center justify-center text-white"
                    aria-label={copy.submitSearch}
                  >
                    <Search size={18} />
                  </button>
                </form>

                {suggestions.length > 0 && (
                  <div className="absolute left-0 top-full z-[350] mt-2 w-full max-w-full overflow-hidden rounded-lg border border-white/20 bg-[#061f34] shadow-[0_22px_60px_rgba(2,10,26,0.5)] ring-1 ring-[#9ce7ee]/20">
                    <ul className="divide-y divide-white/10" role="listbox">
                      {suggestions.map((s) => {
                        const to = toLocalizedPath(cardPath(s.slug));
                        return (
                          <li key={s.slug} role="option" aria-selected="false">
                            <Link
                              to={to}
                              className="flex items-center gap-3 px-3 py-2 normal-case tracking-normal text-white/90 transition hover:bg-white/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery("");
                                setSuggestions([]);
                                setShowMobileMenu(false);
                              }}
                            >
                              {s.image || s.img || s.thumbnail ? (
                                <img
                                  src={s.image || s.img || s.thumbnail}
                                  alt={s._displayName || s.name}
                                  className="h-8 w-auto rounded-sm"
                                  loading="lazy"
                                />
                              ) : (
                                <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-white/10 text-[10px] uppercase">
                                  {copy.cards}
                                </span>
                              )}
                              <span className="text-[16px] font-semibold">{s._displayName || s.name}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowMobileMenu(false)}
                aria-label={copy.closeMenu}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-white/30 bg-[#06263d]/40 text-white shadow-[0_14px_34px_rgba(0,0,0,0.28)] backdrop-blur-md transition hover:bg-white/15"
              >
                <X size={25} />
              </button>
            </div>
          </div>

          <div className={mobileMenuListClass}>
            <Link to={toLocalizedPath("/")} onClick={() => setShowMobileMenu(false)} className={mobileLinkClass}>
              <span className={mobileIconClass}>
                <Home size={17} strokeWidth={1.7} />
              </span>
              <span className={mobileLabelClass}>{copy.home}</span>
            </Link>
            <Link
              to={toLocalizedPath("/what-is-tarot-cards-complete-guide")}
              onClick={() => setShowMobileMenu(false)}
              className={mobileLinkClass}
            >
              <span className={mobileIconClass}>
                <BookOpenText size={17} strokeWidth={1.7} />
              </span>
              <span className={mobileLabelClass}>{copy.whatIsTarot}</span>
            </Link>
            <div className="rounded-md">
              <button
                type="button"
                onClick={() => setShowMobileCards(!showMobileCards)}
                className={`${mobileLinkClass} gap-2 ${showMobileCards ? activeMobileLinkClass : ""}`}
                aria-expanded={showMobileCards ? "true" : "false"}
              >
                <span className={mobileIconClass}>
                  <Layers3 size={17} strokeWidth={1.7} />
                </span>
                <span className={mobileLabelClass}>
                  <span>{copy.tarotCards}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${showMobileCards ? "rotate-180 text-[#9ce7ee]" : ""}`}
                  />
                </span>
              </button>
              {showMobileCards && (
                <div className={`${mobilePanelClass} divide-y divide-white/10`}>
                  {TAROT_SUITS.map((item) => (
                    <Link
                      key={item.slug}
                      to={toLocalizedPath(item.slug)}
                      onClick={() => setShowMobileMenu(false)}
                      className={mobilePanelLinkClass}
                    >
                      <span className={mobileSubCueClass} />
                      <span className="flex min-w-0 flex-1 items-center justify-start gap-2">
                        <span>{isSpanishPath ? item.es : item.name}</span>
                        <ChevronRight size={15} strokeWidth={1.8} className={menuArrowClass} />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-md">
              <button
                type="button"
                onClick={() => setShowMobileEmotions(!showMobileEmotions)}
                className={`${mobileLinkClass} gap-2 ${showMobileEmotions ? activeMobileLinkClass : ""}`}
                aria-expanded={showMobileEmotions ? "true" : "false"}
              >
                <span className={mobileIconClass}>
                  <Heart size={17} strokeWidth={1.7} />
                </span>
                <span className={mobileLabelClass}>
                  <span>{copy.emotions}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${showMobileEmotions ? "rotate-180 text-[#9ce7ee]" : ""}`}
                  />
                </span>
              </button>
              {showMobileEmotions && (
                <div className={`${mobilePanelClass} divide-y divide-white/10`}>
                  {EMOTIONS.map((emotion) => {
                    const slug = emotion.name.toLowerCase().replace(/ /g, "-");
                    return (
                      <Link
                        key={emotion.name}
                        to={toLocalizedPath(`/emotions/${slug}`)}
                        onClick={() => setShowMobileMenu(false)}
                        className={mobilePanelLinkClass}
                      >
                        <span className={mobileSubCueClass} />
                        <span className="flex min-w-0 flex-1 items-center justify-start gap-2">
                          <span>{isSpanishPath ? emotion.es : emotion.name}</span>
                          <ChevronRight size={15} strokeWidth={1.8} className={menuArrowClass} />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            <Link
              to={toLocalizedPath("/yes-no-tarot")}
              onClick={() => setShowMobileMenu(false)}
              className={mobileLinkClass}
            >
              <span className={mobileIconClass}>
                <CircleHelp size={17} strokeWidth={1.7} />
              </span>
              <span className={mobileLabelClass}>{isSpanishPath ? "Tarot si o no" : "Yes/No Tarot"}</span>
            </Link>
            <Link
              to={toLocalizedPath("/readmycard")}
              onClick={() => setShowMobileMenu(false)}
              className={mobileLinkClass}
            >
              <span className={mobileIconClass}>
                <Eye size={17} strokeWidth={1.7} />
              </span>
              <span className={mobileLabelClass}>{copy.readMyCard}</span>
            </Link>

            <div className="rounded-md">
              <button
                type="button"
                onClick={() => setShowMobileTarotscope(!showMobileTarotscope)}
                className={`${mobileLinkClass} gap-2 ${showMobileTarotscope ? activeMobileLinkClass : ""}`}
                aria-expanded={showMobileTarotscope ? "true" : "false"}
              >
                <span className={mobileIconClass}>
                  <Sparkles size={17} strokeWidth={1.7} />
                </span>
                <span className={mobileLabelClass}>
                  <span>{copy.tarotscope}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${showMobileTarotscope ? "rotate-180 text-[#9ce7ee]" : ""}`}
                  />
                </span>
              </button>
              {showMobileTarotscope && (
                <div className={`${mobilePanelClass} divide-y divide-white/10`}>
                  {ZODIAC_SIGNS.map((sign) => (
                    <Link
                      key={sign.slug}
                      to={toLocalizedPath(`/tarotscope/${sign.slug}`)}
                      onClick={() => setShowMobileMenu(false)}
                      className={mobilePanelLinkClass}
                    >
                      <span className={mobileSubCueClass} />
                      <span className="flex min-w-0 flex-1 items-center justify-start gap-2">
                        <span>{isSpanishPath ? sign.es : sign.name}</span>
                        <ChevronRight size={15} strokeWidth={1.8} className={menuArrowClass} />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to={toLocalizedPath("/fate-friday")}
              onClick={() => setShowMobileMenu(false)}
              className={mobileLinkClass}
            >
              <span className={mobileIconClass}>
                <CalendarHeart size={17} strokeWidth={1.7} />
              </span>
              <span className={mobileLabelClass}>{copy.fateFriday}</span>
            </Link>
            <Link
              to={toLocalizedPath("/full-moon")}
              onClick={() => setShowMobileMenu(false)}
              className={mobileLinkClass}
            >
              <span className={mobileIconClass}>
                <MoonStar size={17} strokeWidth={1.7} />
              </span>
              <span className={mobileLabelClass}>{copy.fullMoon}</span>
            </Link>
          </div>
        </aside>
      )}
    </>
  );
}

